import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_MENU_BY_RESTAURANT } from "../apollo/Queries";
import { Plus, X, Utensils, Clock, MapPin, Phone, Mail } from "lucide-react";
import AddMenuModal from "./AddMenuModal";
import { BASE_URL } from "../store/urls";

export default function RestaurantPreview({ isOpen, onClose, restaurant }) {
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [editingMenu, setEditingMenu] = useState(null);

    const { data, loading } = useQuery(GET_MENU_BY_RESTAURANT, {
        variables: { uuid: restaurant?.uuid || "" },
        skip: !restaurant?.uuid,
    });

    if (!isOpen || !restaurant) return null;

    const menus = data?.menuByRestaurant?.content || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-lg">
            <div className="bg-white w-full max-w-5xl h-[90vh] rounded-[3rem] shadow-2xl relative overflow-hidden flex flex-col animate-scale-in">

                {/* Header Header */}
                <div className="relative h-64 flex-shrink-0">
                    <img
                        src={`${BASE_URL}${restaurant.coverPhoto}` || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop"}
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-12">
                        <div className="space-y-4 w-full">
                            <div className="flex justify-between items-end">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-black uppercase tracking-[0.4em] text-premium-gold/90">Curated Destination</span>
                                    <h2 className="text-5xl font-black text-white tracking-tighter">{restaurant.name}</h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="bg-white/10 hover:bg-white/20 backdrop-blur-md p-4 rounded-2xl text-white transition-all transform hover:rotate-90"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* Sidebar: Details */}
                    <div className="w-full md:w-80 bg-gray-50/50 p-10 border-r border-gray-100 space-y-8 overflow-y-auto">
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-premium-teal/40">Information Registry</h3>
                                <div className="space-y-4">
                                    <DetailItem icon={<MapPin size={14} />} label="Location" value={restaurant.address} />
                                    <DetailItem icon={<Phone size={14} />} label="Telephone" value={restaurant.phone} />
                                    <DetailItem icon={<Mail size={14} />} label="Email" value={restaurant.email} />
                                    <DetailItem icon={<Clock size={14} />} label="Working Hours" value={`${restaurant.openTime} — ${restaurant.closingTime}`} />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-premium-teal/40">Description</h3>
                                <p className="text-xs text-gray-400 font-light leading-relaxed">
                                    {restaurant.description || "The essence of this destination is yet to be fully documented in our registry."}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main: Menu Listing */}
                    <div className="flex-1 p-10 overflow-y-auto flex flex-col">
                        <div className="flex justify-between items-center mb-10">
                            <div className="space-y-1">
                                <h3 className="text-2xl font-black text-premium-teal tracking-tight">Available Menu</h3>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Registered Menus</p>
                            </div>
                            <button
                                onClick={() => {
                                    setEditingMenu(null);
                                    setIsAddMenuOpen(true);
                                }}
                                className="bg-premium-teal text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-premium-teal/20 hover:scale-105 transition-all flex items-center gap-2"
                            >
                                <Plus size={14} className="text-premium-gold" />
                                Add Menu
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
                                <div className="w-12 h-12 border-4 border-premium-gold/10 border-t-premium-gold rounded-full animate-spin"></div>
                                <p className="text-[10px] font-black text-premium-gold uppercase tracking-[0.3em]">Synchronizing Menu...</p>
                            </div>
                        ) : menus.length > 0 ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {menus.map((menu) => (
                                    <div
                                        key={menu.uuid}
                                        className="group bg-white border border-gray-100 p-6 rounded-[2rem] hover:shadow-2xl hover:shadow-gray-200/50 transition-all cursor-pointer"
                                        onClick={() => {
                                            setEditingMenu(menu);
                                            setIsAddMenuOpen(true);
                                        }}
                                    >
                                        <div className="flex gap-6">
                                            <div className="w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-50">
                                                <img
                                                    src={`${BASE_URL}${menu.photos?.[0]}` || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=1760&auto=format&fit=crop"}
                                                    alt={menu.title}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <h4 className="font-black text-premium-teal tracking-tight leading-tight">{menu.title}</h4>
                                                    <span className="text-xs font-black text-premium-gold bg-premium-gold/10 px-3 py-1 rounded-full">${menu.price}</span>
                                                </div>
                                                <p className="text-[10px] text-gray-400 line-clamp-2 leading-relaxed">{menu.description}</p>
                                                <div className="pt-2 flex flex-wrap gap-1">
                                                    {menu.ingredients?.slice(0, 3).map((ing, i) => (
                                                        <span key={i} className="text-[8px] font-black uppercase tracking-[0.1em] text-gray-300 bg-gray-50 px-2 py-0.5 rounded-sm">
                                                            {ing}
                                                        </span>
                                                    ))}
                                                    {(menu.ingredients?.length > 3) && <span className="text-[8px] font-black text-gray-300">+{menu.ingredients.length - 3}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center py-20 grayscale opacity-40">
                                <Utensils size={48} className="text-gray-200 mb-6" />
                                <p className="text-gray-400 font-light italic">No gastronomic artifacts have been cataloged for this hub.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddMenuModal
                isOpen={isAddMenuOpen}
                onClose={() => {
                    setIsAddMenuOpen(false);
                    setEditingMenu(null);
                }}
                restaurantUuid={restaurant.uuid}
                initialData={editingMenu}
            />
        </div>
    );
}

function DetailItem({ icon, label, value }) {
    return (
        <div className="flex items-center gap-4 text-xs">
            <div className="w-8 h-8 rounded-xl bg-premium-gold/5 flex items-center justify-center text-premium-gold flex-shrink-0">
                {icon}
            </div>
            <div className="overflow-hidden">
                <p className="text-[8px] font-black uppercase tracking-widest text-gray-300 mb-0.5">{label}</p>
                <p className="font-bold text-premium-teal truncate">{value}</p>
            </div>
        </div>
    );
}
